// Foreground UX path: keep total added latency small. Two retries with short
// backoff (200ms, 400ms) plus jitter — enough to ride out a brief blip
// (sleep/wake, wifi/VPN/proxy transition, DNS/TLS hiccup) without making the
// user wait on a hard error. See STX1-622.
const DEFAULT_MAX_ATTEMPTS = 3
const DEFAULT_BASE_DELAY_MS = 200

interface ITransientRetryOptions {
  /** Total attempts including the first try. Default 3. */
  maxAttempts?: number
  /** Base backoff in ms; attempt N waits ~baseDelayMs * 2^(N-1) + jitter. */
  baseDelayMs?: number
  /**
   * Caller AbortSignal — an abort from this is terminal, never retried, and
   * cuts short an in-flight backoff so the abort is observed at the bound.
   */
  signal?: AbortSignal
}

/**
 * Backoff sleep that ends as soon as `signal` aborts (STX1-1002). A plain timer
 * here would defer an abort until the sleep finished, which matters when the
 * caller's signal is a request-timeout bounding something held for the duration
 * — e.g. the refresh POST holds a cross-tab Web Lock whose only bound is that
 * timeout, so a late abort could hold it past the bound. Resolves rather than
 * rejects: the loop's top-of-turn guard is what turns an abort into the error.
 */
const delay = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise<void>(resolve => {
    if (signal?.aborted) {
      resolve()
      return
    }
    const onAbort = (): void => {
      clearTimeout(timer)
      resolve()
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })

/** A thrown fetch failure caused by the caller aborting — must not be retried. */
function isCallerAbort(error: unknown, signal?: AbortSignal): boolean {
  return (
    !!signal?.aborted && error instanceof Error && error.name === 'AbortError'
  )
}

/**
 * Run `doFetch` with retry on transient failures: network errors / timeouts
 * (fetch throws) and HTTP 5xx. 4xx responses are returned as-is (the 401 →
 * refresh path is handled separately by withRefreshRetry); only genuinely
 * transient failures are retried. A caller-supplied AbortSignal is respected —
 * an abort from it is terminal.
 */
export async function fetchWithTransientRetry(
  doFetch: () => Promise<Response>,
  options: ITransientRetryOptions = {},
): Promise<Response> {
  const maxAttempts = options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS
  const baseDelayMs = options.baseDelayMs ?? DEFAULT_BASE_DELAY_MS
  const { signal } = options

  let lastError: unknown
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (signal?.aborted) {
      throw lastError ?? new DOMException('Aborted', 'AbortError')
    }

    try {
      const response = await doFetch()
      if (response.status < 500 || attempt === maxAttempts) {
        return response
      }
    } catch (error) {
      // Caller aborted, or this was the last attempt — propagate.
      if (isCallerAbort(error, signal) || attempt === maxAttempts) {
        throw error
      }
      lastError = error
    }

    // Exponential backoff with full jitter, e.g. 200ms then 400ms (± jitter).
    // Abort-aware, so a caller timeout firing mid-sleep is observed at the bound
    // rather than after the sleep.
    const backoff = baseDelayMs * 2 ** (attempt - 1)
    await delay(backoff / 2 + Math.random() * (backoff / 2), signal)
  }

  // Unreachable: the loop returns or throws on the final attempt.
  throw lastError
}
